"use client";

import Link from "next/link";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import { useEffect, useState, useRef } from "react";
import { Menu, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CostDocLogo } from "@/components/site/CostDocLogo";
import { cn } from "@/lib/utils";
import {
  DESKTOP_PRIMARY_RAIL,
  DESKTOP_SECONDARY_RAIL,
  NAV_PRIMARY_CTA,
  PRIMARY_NAV_ITEMS,
  SECONDARY_NAV_ITEMS,
} from "@/lib/data/site-nav";

const AuthButton = dynamic(
  () => import("@/components/site/AuthButton").then((mod) => mod.AuthButton),
  {
    ssr: false,
    loading: () => <div className="h-11 w-24 rounded-xl bg-transparent" aria-hidden="true" />,
  },
);

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const touchStartYRef = useRef<number | null>(null);
  const pathname = usePathname();

  // Auto-close mobile menu on SPA navigation (route change)
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 16);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handle body scroll lock + focus management
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
      // Focus first link in menu
      const firstLink = menuRef.current?.querySelector<HTMLElement>("a");
      firstLink?.focus();
    } else {
      document.body.style.overflow = "";
    }
  }, [isMobileMenuOpen]);

  // ESC key to close mobile menu
  useEffect(() => {
    if (!isMobileMenuOpen) return;
    function handleEsc(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setIsMobileMenuOpen(false);
        toggleRef.current?.focus();
      }
    }
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (!isMobileMenuOpen) return;

    function closeMobileMenu() {
      setIsMobileMenuOpen(false);
      touchStartYRef.current = null;
    }

    function handlePointerDown(event: PointerEvent) {
      const target = event.target as Node | null;
      if (!target) return;
      if (menuRef.current?.contains(target) || toggleRef.current?.contains(target)) {
        return;
      }
      closeMobileMenu();
    }

    function handleTouchStart(event: TouchEvent) {
      touchStartYRef.current = event.touches[0]?.clientY ?? null;
    }

    function handleTouchMove(event: TouchEvent) {
      const startY = touchStartYRef.current;
      const currentY = event.touches[0]?.clientY;
      if (startY == null || currentY == null) return;
      if (startY - currentY > 24) {
        closeMobileMenu();
      }
    }

    function handleWheel(event: WheelEvent) {
      if (event.deltaY > 12) {
        closeMobileMenu();
      }
    }

    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("touchstart", handleTouchStart, { passive: true });
    document.addEventListener("touchmove", handleTouchMove, { passive: true });
    document.addEventListener("wheel", handleWheel, { passive: true });
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("wheel", handleWheel);
    };
  }, [isMobileMenuOpen]);

  return (
    <nav
      aria-label="Primary"
      className={cn(
        "fixed top-0 z-50 w-full transition-[transform,box-shadow,border-color] duration-500",
        isScrolled
          ? "bg-white/95 backdrop-blur-md pt-5 pb-3 border-b border-black/5 shadow-sm"
          : "bg-white/80 backdrop-blur-sm pt-8 pb-5",
      )}
    >
      <div className="container max-w-[1400px] flex items-center justify-between gap-6">
        <Link
          href="/"
          className="group flex min-h-[56px] flex-col items-start justify-center gap-1 py-1"
          aria-label="CostDoc home"
        >
          <CostDocLogo priority imageClassName="h-9 w-auto sm:h-10 md:h-11" />
          <span className="pl-0.5 text-[9px] font-semibold uppercase tracking-[0.18em] text-slate-500 sm:text-[10px]">
            Independent Australian Data
          </span>
        </Link>

        {/* Desktop Navigation. Hidden via display:none below xl, so anchors are not in the tab order at mobile breakpoints. */}
        <div className="hidden items-center gap-1 rounded-xl border border-black/10 bg-white/90 backdrop-blur-sm px-2 py-1.5 shadow-lg xl:flex">
          {DESKTOP_PRIMARY_RAIL.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className="inline-flex items-center min-h-[44px] px-4 py-2 text-xs font-medium text-slate-500 hover:text-slate-900 hover:bg-slate-50 transition-colors uppercase tracking-[0.12em] rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600/40"
            >
              {item.label}
            </Link>
          ))}
          <div className="h-4 w-px bg-black/8 mx-1.5" aria-hidden="true" />
          {DESKTOP_SECONDARY_RAIL.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className="inline-flex items-center min-h-[44px] px-3 py-2 text-xs font-medium text-slate-500 hover:text-slate-900 hover:bg-slate-50 transition-colors uppercase tracking-[0.12em] rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600/40"
            >
              {item.label}
            </Link>
          ))}
          <Button asChild size="sm" className="ml-1 uppercase text-xs tracking-wider rounded-lg">
            <Link href={NAV_PRIMARY_CTA.href}>{NAV_PRIMARY_CTA.label}</Link>
          </Button>
          <AuthButton />
        </div>

        {/* Mobile menu toggle */}
        <button
          ref={toggleRef}
          type="button"
          className="rounded-2xl border border-black/5 bg-white/90 p-3 text-slate-900 shadow-sm xl:hidden z-[60]"
          onClick={() => setIsMobileMenuOpen((current) => !current)}
          aria-label="Toggle Navigation"
          aria-expanded={isMobileMenuOpen}
        >
          {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile Menu Backdrop */}
      <div
        className={cn(
          "fixed inset-0 bg-black/20 backdrop-blur-sm transition-opacity z-[55] xl:hidden",
          isMobileMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        onClick={() => setIsMobileMenuOpen(false)}
      />

      {/* Mobile Menu Content.
          - aria-hidden when closed → screen readers skip duplicate links
          - inert when closed → ALL descendants removed from tab order (covers AuthButton etc.)
          - visibility: hidden when closed → Playwright / AT can confirm truly hidden
          - max-height: 100dvh - top offset → drawer never overflows viewport on any mobile width
          - overflow-y: auto → internal scroll if content taller than clamped height
      */}
      <div
        ref={menuRef}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        aria-hidden={!isMobileMenuOpen}
        // `inert` (React 19 / HTML spec) removes all descendants from tab order
        // and AT without unmounting the element. Covers AuthButton and all nested links.
        inert={!isMobileMenuOpen}
        data-mobile-nav
        className={cn(
          "fixed inset-x-4 top-24 z-[60] origin-top rounded-md border border-black/5 bg-white p-8 shadow-2xl",
          "overflow-y-auto",
          "transition-[transform,opacity,visibility] duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)] xl:hidden",
          // Constrain height so content never overflows viewport. 6rem = top-24 navbar offset.
          "max-h-[calc(100dvh-7rem)]",
          isMobileMenuOpen
            ? "translate-y-0 opacity-100 scale-100 visible pointer-events-auto"
            : "-translate-y-4 opacity-0 scale-95 invisible pointer-events-none"
        )}
      >
        <div className="flex flex-col">
          <div className="flex flex-wrap gap-2 mb-10">
            <Badge className="bg-emerald-50 text-emerald-700 border-none font-semibold uppercase text-xs tracking-widest px-4 py-1.5">Patient Reported</Badge>
            <Badge className="bg-slate-50 text-slate-500 border-none font-semibold uppercase text-xs tracking-widest px-4 py-1.5">Anonymous</Badge>
          </div>

          <nav className="flex flex-col gap-2">
            {PRIMARY_NAV_ITEMS.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="inline-flex items-center min-h-[44px] py-3 text-3xl font-bold tracking-tighter text-slate-900 uppercase"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}

            <div className="h-px bg-black/5 my-4" aria-hidden="true" />

            {SECONDARY_NAV_ITEMS.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="inline-flex items-center min-h-[44px] py-3 text-xl font-bold text-slate-900 uppercase tracking-tight"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="mt-12 pt-8 border-t border-black/5 space-y-4">
            <Button asChild size="lg" className="w-full text-xl uppercase tracking-widest">
              <Link
                href={NAV_PRIMARY_CTA.href}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {NAV_PRIMARY_CTA.label}
              </Link>
            </Button>
            <AuthButton variant="mobile" />
          </div>
        </div>
      </div>
    </nav>
  );
}
